/* eslint-disable max-nested-callbacks */
/* eslint-disable promise/prefer-await-to-callbacks */
import { PayloadAction } from '@reduxjs/toolkit';
import { StorageKeyEnum } from 'enums/StorageKeyEnum';
import moment from 'moment';
import { combineEpics, Epic, ofType } from 'redux-observable';
import { from, of } from 'rxjs';
import { catchError, pluck, switchMap } from 'rxjs/operators';
import { AppState } from 'store';
import { dialogClose } from 'store/dialog/dialog.actions';
import { api } from 'utils/axiosInstance';
import { parseAutocomplete } from 'utils/formatters/parseAutocomplete';
import { downloadBlobFile } from 'utils/handlers/fileDownloadHandler';
import { apiErrorHandler, notify } from 'utils/handlers/notificationHandler';
import { getItem } from 'utils/handlers/storageHandler';
import { ChangedKontentRequest, JobsErrorCodeIgnoreDTO, Page, ProjectDTO } from 'utils/restApplicationClient';
import {
  changeDueDateJob,
  changeDueDateJobError,
  changeDueDateJobSuccess,
  changeDueDateProject,
  changeDueDateProjectError,
  changeDueDateProjectSuccess,
  checkErrorProjectSuccess,
  checkProject,
  checkProjectClear,
  checkProjectSuccess,
  contentChanged,
  contentChangedError,
  contentChangedIgnore,
  contentChangedSend,
  contentChangedSuccess,
  contentChangedUpdate,
  contentRemovedIgnore,
  contentRemovedIgnoreError,
  contentRemovedIgnoreSuccess,
  contentRemovedRemove,
  contentRemovedRemoveError,
  contentRemovedRemoveSuccess,
  errorMessageIgnore,
  errorMessageIgnoreError,
  errorMessageIgnoreSuccess,
  exportListToFile,
  exportListToFileError,
  exportListToFileSuccess,
  getProjectChangedContent,
  getProjectChangedContentError,
  getProjectChangedContentSuccess,
  getProjectLanguages,
  getProjectLanguagesError,
  getProjectLanguagesSuccess,
  getProjects,
  getProjectsError,
  getProjectsSuccess,
  getProjectWorkflowSteps,
  getProjectWorkflowStepsError,
  getProjectWorkflowStepsSuccess,
  getXTMCustomersForUser,
  getXTMCustomersForUserError,
  getXTMCustomersForUserSuccess,
  getXTMRequestersForProject,
  getXTMRequestersForProjectError,
  getXTMRequestersForProjectSuccess,
  modifyLanguages,
  modifyLanguagesError,
  modifyLanguagesSuccess,
  setProjectSearch,
  setProjectsPagination,
} from './projects.actions';
import {
  FileCheckArray,
  IProjectCheckPayload,
  IProjectState,
  LanguageCheckArray,
  LanguageErrorArray,
  ModifyLanguagesPayload,
  ProjectCheckArray,
  ProjectErrorArray,
} from './projects.interface';

const setProjectsPaginationEpic: Epic = (action$) =>
  action$.pipe(
    ofType(setProjectsPagination.type),
    switchMap(() => of(getProjects())),
  );

const checkProjectEpic: Epic = (action$, state$) =>
  action$.pipe(
    ofType(checkProject.type),
    pluck<PayloadAction<IProjectCheckPayload>, 'payload'>('payload'),
    switchMap(({ projects }) => {
      const { projectCheckArray, data } = state$.value.projects as IProjectState;
      const temporaryProjects: ProjectCheckArray = {
        checked: projectCheckArray.checked,
        indeterminate: projectCheckArray.indeterminate,
        projects: { ...projectCheckArray.projects },
      };

      projects?.forEach(({ id: projectId, languages, status: projectStatus }) => {
        const project = data?.find(({ id }) => id === projectId);
        const temporaryLanguages: LanguageCheckArray = { ...projectCheckArray.projects[projectId]?.languages } || {};

        languages?.forEach(({ id: language, files, status: languageStatus }) => {
          const projectLanguage = project?.targetLanguages.find(
            ({ language: languageCode }) => languageCode === language,
          );
          const temporaryFiles: FileCheckArray =
            { ...projectCheckArray.projects[projectId]?.languages[language]?.files } || {};

          files?.forEach(({ id: fileId, checked, status: fileStatus }) => {
            temporaryFiles[fileId] = {
              checked,
              status: fileStatus,
            };
          });

          const checkedFilesLength = Object.values(temporaryFiles).filter(({ checked }) => checked).length;
          const maxFilesLength = projectLanguage?.files.length;
          const isLanguageIndeterminate = checkedFilesLength !== maxFilesLength && checkedFilesLength > 0;

          temporaryLanguages[language] = {
            checked: !isLanguageIndeterminate && checkedFilesLength > 0,
            indeterminate: isLanguageIndeterminate,
            files: temporaryFiles,
            status: languageStatus,
          };
        });

        const checkedLanguagesLength = Object.values(temporaryLanguages).filter(({ checked }) => checked).length;
        const indeterminateLanguagesLength = Object.values(temporaryLanguages).filter(
          ({ indeterminate }) => indeterminate,
        ).length;
        const maxLanguagesLength = project?.targetLanguages.length;
        const isProjectIndeterminate =
          (checkedLanguagesLength !== maxLanguagesLength && checkedLanguagesLength > 0) ||
          indeterminateLanguagesLength > 0;

        temporaryProjects.projects[projectId] = {
          languages: temporaryLanguages,
          checked: !isProjectIndeterminate && checkedLanguagesLength > 0,
          indeterminate: isProjectIndeterminate,
          status: projectStatus,
        };
      });
      const checkedProjectsLength = Object.values(temporaryProjects.projects).filter(({ checked }) => checked).length;
      const indeterminateProjectsLength = Object.values(temporaryProjects.projects).filter(
        ({ indeterminate }) => indeterminate,
      ).length;
      const maxProjectsLength = data?.length;
      const isProjectsIndeterminate =
        (checkedProjectsLength !== maxProjectsLength && checkedProjectsLength > 0) || indeterminateProjectsLength > 0;

      temporaryProjects.checked = !isProjectsIndeterminate && checkedProjectsLength > 0;
      temporaryProjects.indeterminate = isProjectsIndeterminate;

      return of(checkProjectSuccess(temporaryProjects));
    }),
  );

const setProjectSearchEpic: Epic = (action$) =>
  action$.pipe(
    ofType(setProjectSearch.type),
    switchMap(() => of(getProjects())),
  );

const getProjectsEpic: Epic = (action$, state$) =>
  action$.pipe(
    ofType(getProjects.type),
    switchMap(() => {
      const {
        search,
        pagination: {
          order,
          orderBy,
          page,
          size,
          jobId,
          projectId,
          targetLanguages,
          sourceLanguages,
          workflowSteps,
          customers,
          requesters,
          forceUpdate,
          ...rest
        },
      } = state$.value.projects as IProjectState;
      const kontentProject = getItem(StorageKeyEnum.projectId);

      return from(
        api.filterProjects(
          {
            projectId: projectId ? Number(projectId) : undefined,
            jobId: jobId ? Number(jobId) : undefined,
            targetLanguages: targetLanguages?.map(({ value }) => value),
            sourceLanguages: sourceLanguages?.map(({ value }) => value),
            workflowSteps: workflowSteps?.map(({ value }) => value),
            projectName: search,
            customerIds: customers?.map(({ value }) => Number(value)),
            requesterUUIDs: requesters?.map(({ value }) => value),
            ...rest,
          },
          {
            page,
            size,
            sort: `${orderBy},${order}`,
            kontentProjectUUID: kontentProject as string,
            forceUpdate: forceUpdate || false,
          },
        ),
      ).pipe(
        switchMap(({ data }) => {
          return of(getProjectsSuccess(data), checkProjectClear());
        }),
        catchError((error) => {
          apiErrorHandler(error);
          return of(getProjectsError());
        }),
      );
    }),
  );

const exportListToFileEpic: Epic = (action$, state$) =>
  action$.pipe(
    ofType(exportListToFile),
    switchMap(() => {
      const {
        /* eslint-disable-next-line */
        pagination: { page, size, order, orderBy, search, ...rest },
      } = (state$.value as AppState).projects;
      const kontentProjectUUID = getItem(StorageKeyEnum.projectId);

      return from(
        api.generateReport(rest, { kontentProjectUUID: kontentProjectUUID as string }, { responseType: 'blob' }),
      ).pipe(
        switchMap(({ data }) => {
          const fileName = `XTM_Kontent_${moment().format('YYYYMMDD_HHmm')}`;

          downloadBlobFile((data as unknown) as Blob, fileName);
          return of(exportListToFileSuccess((data as unknown) as Blob));
        }),
        catchError((error) => {
          apiErrorHandler(error);
          return of(exportListToFileError());
        }),
      );
    }),
  );

const contentChangedEpic: Epic = (action$) =>
  action$.pipe(
    ofType(contentChanged.type),
    pluck<PayloadAction<ChangedKontentRequest>, 'payload'>('payload'),
    switchMap((payload) =>
      from(
        payload.productIds
          ? api.performActionForChangedKontentProjects(payload)
          : api.performActionForChangedKontentJobs(payload),
      ).pipe(
        switchMap(() => {
          notify('successmessage.contentChangedUpdate', 'success');
          return of(contentChangedSuccess(), dialogClose(), getProjects());
        }),
        catchError((error) => {
          apiErrorHandler(error);
          return of(contentChangedError());
        }),
      ),
    ),
  );

const contentChangedSendEpic: Epic = (action$) =>
  action$.pipe(
    ofType(contentChangedSend.type),
    pluck<PayloadAction<ChangedKontentRequest>, 'payload'>('payload'),
    switchMap((payload) => of(contentChanged(payload))),
  );

const contentChangedUpdateEpic: Epic = (action$) =>
  action$.pipe(
    ofType(contentChangedUpdate.type),
    pluck<PayloadAction<ChangedKontentRequest>, 'payload'>('payload'),
    switchMap((payload) => of(contentChanged(payload))),
  );

const contentChangedIgnoreEpic: Epic = (action$) =>
  action$.pipe(
    ofType(contentChangedIgnore.type),
    pluck<PayloadAction<ChangedKontentRequest>, 'payload'>('payload'),
    switchMap((payload) => of(contentChanged(payload))),
  );

const getProjectChangedContentEpic: Epic = (action$) =>
  action$.pipe(
    ofType(getProjectChangedContent.type),
    pluck<PayloadAction<{ projectId: string; sort: boolean }>, 'payload'>('payload'),
    switchMap(({ projectId, sort }) =>
      from(api.getChangedKontentProducts(projectId, { sort })).pipe(
        switchMap(({ data }) => of(getProjectChangedContentSuccess(data))),
        catchError((error) => {
          apiErrorHandler(error);
          return of(getProjectChangedContentError());
        }),
      ),
    ),
  );

const contentRemovedRemoveEpic: Epic = (action$) =>
  action$.pipe(
    ofType(contentRemovedRemove),
    switchMap(({ payload: { jobIds } }) => {
      //@TODO wait for be
      return from(
        new Promise((resolve) => {
          setTimeout(() => {
            resolve({ jobIds });
          }, 2000);
        }),
      ).pipe(
        switchMap(() => {
          notify('successmessage.contentRemovedRemove', 'success');
          return of(contentRemovedRemoveError(), dialogClose());
        }),
        catchError((error) => {
          apiErrorHandler(error);
          return of(contentRemovedRemoveSuccess());
        }),
      );
    }),
  );

const contentRemovedIgnoreEpic: Epic = (action$) =>
  action$.pipe(
    ofType(contentRemovedIgnore),
    switchMap(({ payload: { jobIds } }) => {
      //@TODO wait for be
      return from(
        new Promise((resolve) => {
          setTimeout(() => {
            resolve({ jobIds });
          }, 2000);
        }),
      ).pipe(
        switchMap(() => {
          notify('successmessage.contentRemovedIgnore', 'success');
          return of(contentRemovedIgnoreError(), dialogClose());
        }),
        catchError((error) => {
          apiErrorHandler(error);
          return of(contentRemovedIgnoreSuccess());
        }),
      );
    }),
  );

const errorMessageIgnoreEpic: Epic = (action$) =>
  action$.pipe(
    ofType(errorMessageIgnore.type),
    pluck<PayloadAction<JobsErrorCodeIgnoreDTO>, 'payload'>('payload'),
    switchMap((payload) => {
      return from(api.ignoreErrorCode(payload)).pipe(
        switchMap(() => {
          notify('successmessage.projectCancelledIgnore', 'success');
          return of(errorMessageIgnoreError(), dialogClose(), getProjects());
        }),
        catchError((error) => {
          apiErrorHandler(error);
          return of(errorMessageIgnoreSuccess());
        }),
      );
    }),
  );

const changeDueDateJobEpic: Epic = (action$) =>
  action$.pipe(
    ofType(changeDueDateJob.type),
    pluck<
      PayloadAction<{
        jobId: string;
        dueDate: Date;
      }>,
      'payload'
    >('payload'),
    switchMap(({ jobId, dueDate }) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return from(api.updateDueDateForJob(jobId, { newDueDate: dueDate.toISOString() as any })).pipe(
        switchMap(() => {
          notify('successmessage.changeDueDate', 'success');
          return of(changeDueDateJobSuccess(), dialogClose(), getProjects());
        }),
        catchError((error) => {
          apiErrorHandler(error);
          return of(changeDueDateJobError());
        }),
      );
    }),
  );

const changeDueDateProjectEpic: Epic = (action$) =>
  action$.pipe(
    ofType(changeDueDateProject.type),
    pluck<
      PayloadAction<{
        projectId: string;
        dueDate: Date;
      }>,
      'payload'
    >('payload'),
    switchMap(({ projectId, dueDate }) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return from(api.updateDueDateForProject(projectId, { newDueDate: dueDate.toISOString() as any })).pipe(
        switchMap(() => {
          notify('successmessage.changeDueDate', 'success');
          return of(changeDueDateProjectSuccess(), dialogClose(), getProjects());
        }),
        catchError((error) => {
          apiErrorHandler(error);
          return of(changeDueDateProjectError());
        }),
      );
    }),
  );

const modifyLanguagesEpic: Epic = (action$) =>
  action$.pipe(
    ofType(modifyLanguages.type),
    pluck<PayloadAction<ModifyLanguagesPayload>, 'payload'>('payload'),
    switchMap(({ projectId, languages, operation }) => {
      //@TODO wait for be
      return from(
        operation === 'ADD'
          ? api.addTargetLanguages(projectId, { newTargetLanguages: languages })
          : api.removeTargetLanguagesTargetLanguages(projectId, { targetLanguagesToRemove: languages }),
      ).pipe(
        switchMap(() => {
          notify(operation === 'ADD' ? 'successmessage.addLanguages' : 'successmessage.removeLanguages', 'success');
          return of(modifyLanguagesSuccess(), dialogClose(), getProjects());
        }),
        catchError((error) => {
          apiErrorHandler(error);
          return of(modifyLanguagesError());
        }),
      );
    }),
  );

const getProjectLanguagesEpic: Epic = (action$) =>
  action$.pipe(
    ofType(getProjectLanguages.type),
    pluck<PayloadAction<string>, 'payload'>('payload'),
    switchMap((projectId) =>
      from(api.getProjectLanguageMapping(projectId)).pipe(
        switchMap(({ data }) => of(getProjectLanguagesSuccess(data))),
        catchError((error) => {
          apiErrorHandler(error);
          return of(getProjectLanguagesError());
        }),
      ),
    ),
  );

const getProjectWorkflowStepsEpic: Epic = (action$) =>
  action$.pipe(
    ofType(getProjectWorkflowSteps.type),
    pluck<PayloadAction<string>, 'payload'>('payload'),
    switchMap(() =>
      from(api.getXTMWorkflowSteps()).pipe(
        switchMap(({ data }) => of(getProjectWorkflowStepsSuccess(data))),
        catchError((error) => {
          apiErrorHandler(error);
          return of(getProjectWorkflowStepsError());
        }),
      ),
    ),
  );

const getXTMRequestersForProjectEpic: Epic = (action$) =>
  action$.pipe(
    ofType(getXTMRequestersForProject.type),
    pluck<PayloadAction<string>, 'payload'>('payload'),
    switchMap((kontentProjectUUID) =>
      from(api.getRequestersForProject({ kontentProjectUUID })).pipe(
        switchMap(({ data }) => of(getXTMRequestersForProjectSuccess(parseAutocomplete(data, 'name', 'id')))),
        catchError((error) => {
          apiErrorHandler(error);
          return of(getXTMRequestersForProjectError());
        }),
      ),
    ),
  );

const getXTMCustomersForUserEpic: Epic = (action$) =>
  action$.pipe(
    ofType(getXTMCustomersForUser.type),
    switchMap(() =>
      from(api.getXTMCustomersForCurrentUser()).pipe(
        switchMap(({ data }) => of(getXTMCustomersForUserSuccess(parseAutocomplete(data, 'name', 'xtmCustomerId')))),
        catchError((error) => {
          apiErrorHandler(error);
          return of(getXTMCustomersForUserError());
        }),
      ),
    ),
  );

const checkProjectErrorEpic: Epic = (action$) =>
  action$.pipe(
    ofType(getProjectsSuccess.type),
    pluck<PayloadAction<Page<ProjectDTO>>, 'payload'>('payload'),
    switchMap((payload) => {
      const projects: ProjectErrorArray = {};

      payload.content.forEach(({ id, targetLanguages }) => {
        const languages: LanguageErrorArray = {};

        targetLanguages.forEach(({ language, files }) => {
          const errorMessage = files.find(({ errorMessage }) => errorMessage)?.errorMessage;

          if (errorMessage) {
            languages[language] = { error: errorMessage };
          }
        });

        const languageKeys = Object.keys(languages);

        if (languageKeys.length > 0) {
          projects[id] = { error: languages[languageKeys[0]].error, languages };
        }
      });
      return of(checkErrorProjectSuccess(projects));
    }),
  );

export const projectsEpics = combineEpics(
  getProjectsEpic,
  exportListToFileEpic,
  contentChangedEpic,
  contentChangedIgnoreEpic,
  changeDueDateJobEpic,
  modifyLanguagesEpic,
  contentChangedSendEpic,
  contentChangedUpdateEpic,
  contentRemovedRemoveEpic,
  contentRemovedIgnoreEpic,
  checkProjectEpic,
  setProjectsPaginationEpic,
  getProjectLanguagesEpic,
  getProjectWorkflowStepsEpic,
  changeDueDateProjectEpic,
  checkProjectErrorEpic,
  errorMessageIgnoreEpic,
  getProjectChangedContentEpic,
  getXTMCustomersForUserEpic,
  getXTMRequestersForProjectEpic,
  setProjectSearchEpic,
);
